import { withStyles } from '@mui/styles';
import React from 'react';
import { connect } from 'react-redux';
import { getAvailableDevices } from '../../base/devices/actions.web';
import AbstractDialogTab from '../../base/dialog/components/web/AbstractDialogTab';
import { translate } from '../../base/i18n/functions';
import { createLocalTrack } from '../../base/lib-jitsi-meet/functions.web';
import Checkbox from '../../base/ui/components/web/Checkbox';
import { iAmVisitor as iAmVisitorCheck } from '../../visitors/functions';
import logger from '../logger';
import AudioInputPreview from './AudioInputPreview';
import AudioOutputPreview from './AudioOutputPreview';
import DeviceHidContainer from './DeviceHidContainer.web';
import DeviceSelector from './DeviceSelector.web';
const styles = (theme) => {
    return {
        container: {
            display: 'flex',
            flexDirection: 'column',
            padding: '0 2px',
            width: '100%'
        },
        inputContainer: {
            marginBottom: theme.spacing(3)
        },
        outputContainer: {
            margin: `${theme.spacing(5)} 0`,
            display: 'flex',
            alignItems: 'flex-end'
        },
        outputButton: {
            marginLeft: theme.spacing(3)
        },
        noiseSuppressionContainer: {
            marginBottom: theme.spacing(5)
        }
    };
};
/**
 * React {@code Component} for previewing audio and video input/output devices.
 *
 * @augments Component
 */
class AudioDevicesSelection extends AbstractDialogTab {
    /**
     * Initializes a new DeviceSelection instance.
     *
     * @param {Object} props - The read-only React Component props with which
     * the new instance is to be initialized.
     */
    constructor(props) {
        super(props);
        this.state = {
            previewAudioTrack: null
        };
        this._unMounted = true;
    }
    /**
     * Generate the initial previews for audio input and video input.
     *
     * @inheritdoc
     */
    componentDidMount() {
        this._unMounted = false;
        Promise.all([
            this._createAudioInputTrack(this.props.selectedAudioInputId)
        ])
            .catch(err => logger.warn('Failed to initialize preview tracks', err))
            .then(() => {
            this.props.dispatch(getAvailableDevices());
        });
    }
    /**
     * Checks if audio / video permissions were granted. Updates audio input and
     * video input previews.
     *
     * @param {Object} prevProps - Previous props this component received.
     * @returns {void}
     */
    componentDidUpdate(prevProps) {
        if (prevProps.selectedAudioInputId
            !== this.props.selectedAudioInputId) {
            this._createAudioInputTrack(this.props.selectedAudioInputId);
        }
    }
    /**
     * Ensure preview tracks are destroyed to prevent continued use.
     *
     * @inheritdoc
     */
    componentWillUnmount() {
        this._unMounted = true;
        this._disposeAudioInputPreview();
    }
    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     */
    render() {
        const { classes, hasAudioPermission, hideAudioInputPreview, hideAudioOutputPreview, hideDeviceHIDContainer, hideNoiseSuppression, iAmVisitor, noiseSuppressionEnabled, selectedAudioOutputId, t } = this.props;
        const { audioInput, audioOutput } = this._getSelectors();
        return (React.createElement("div", { className: classes.container },
            !iAmVisitor && React.createElement("div", { "aria-live": 'polite', className: classes.inputContainer }, this._renderSelector(audioInput)),
            !hideAudioInputPreview && hasAudioPermission && !iAmVisitor
                && React.createElement(AudioInputPreview, { track: this.state.previewAudioTrack }),
            React.createElement("div", { "aria-live": 'polite', className: classes.outputContainer },
                this._renderSelector(audioOutput),
                !hideAudioOutputPreview && hasAudioPermission
                    && React.createElement(AudioOutputPreview, { className: classes.outputButton, deviceId: selectedAudioOutputId })),
            !hideNoiseSuppression && !iAmVisitor && (React.createElement("div", { className: classes.noiseSuppressionContainer },
                React.createElement(Checkbox, { checked: noiseSuppressionEnabled, label: t('toolbar.enableNoiseSuppression'), 
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange: () => super._onChange({
                        noiseSuppressionEnabled: !noiseSuppressionEnabled
                    }) }))),
            !hideDeviceHIDContainer && !iAmVisitor
                && React.createElement(DeviceHidContainer, null)));
    }
    /**
     * Creates the JitsiTrack for the audio input preview.
     *
     * @param {string} deviceId - The id of audio input device to preview.
     * @private
     * @returns {void}
     */
    _createAudioInputTrack(deviceId) {
        const { hideAudioInputPreview } = this.props;
        if (hideAudioInputPreview) {
            return;
        }
        return this._disposeAudioInputPreview()
            .then(() => createLocalTrack('audio', deviceId, 5000))
            .then(jitsiLocalTrack => {
            if (this._unMounted) {
                jitsiLocalTrack.dispose();
                return;
            }
            this.setState({
                previewAudioTrack: jitsiLocalTrack
            });
        })
            .catch(() => {
            this.setState({
                previewAudioTrack: null
            });
        });
    }
    /**
     * Utility function for disposing the current audio input preview.
     *
     * @private
     * @returns {Promise}
     */
    _disposeAudioInputPreview() {
        return this.state.previewAudioTrack
            ? this.state.previewAudioTrack.dispose() : Promise.resolve();
    }
    /**
     * Creates a DeviceSelector instance based on the passed in configuration.
     *
     * @private
     * @param {Object} deviceSelectorProps - The props for the DeviceSelector.
     * @returns {ReactElement}
     */
    _renderSelector(deviceSelectorProps) {
        return deviceSelectorProps ? (React.createElement(DeviceSelector, { ...deviceSelectorProps, key: deviceSelectorProps.id })) : null;
    }
    /**
     * Returns object configurations for audio input and output.
     *
     * @private
     * @returns {Object} Configurations.
     */
    _getSelectors() {
        const { availableDevices, hasAudioPermission } = this.props;
        const audioInput = {
            devices: availableDevices.audioInput,
            hasPermission: hasAudioPermission,
            icon: 'icon-microphone',
            isDisabled: this.props.disableAudioInputChange || this.props.disableDeviceChange,
            key: 'audioInput',
            id: 'audioInput',
            label: 'settings.selectMic',
            onSelect: (selectedAudioInputId) => super._onChange({ selectedAudioInputId }),
            selectedDeviceId: this.state.previewAudioTrack
                ? this.state.previewAudioTrack.getDeviceId() : this.props.selectedAudioInputId
        };
        let audioOutput;
        if (!this.props.hideAudioOutputSelect) {
            audioOutput = {
                devices: availableDevices.audioOutput,
                hasPermission: hasAudioPermission,
                icon: 'icon-speaker',
                isDisabled: this.props.disableDeviceChange,
                key: 'audioOutput',
                id: 'audioOutput',
                label: 'settings.selectAudioOutput',
                onSelect: (selectedAudioOutputId) => super._onChange({ selectedAudioOutputId }),
                selectedDeviceId: this.props.selectedAudioOutputId
            };
        }
        return { audioInput,
            audioOutput };
    }
}
const mapStateToProps = (state) => {
    return {
        availableDevices: state['features/base/devices'].availableDevices ?? {},
        iAmVisitor: iAmVisitorCheck(state)
    };
};
export default connect(mapStateToProps)(withStyles(styles)(translate(AudioDevicesSelection)));
